import { debounce, isAppleDevice } from './utils';
import { SofiaMode, TermsAndConditionsState } from '../types';
import initParams from '../store/initParams';

/**
 * Construye la clase de animcion a partir de los parametros
 * @param customDelay
 * @param animation
 * @param isInfinite
 */
export function getAnimationClass(customDelay: number, animation = 'headShake', isInfinite: boolean): string[] {
  return [
    'sofia_animate__animated',
    isInfinite ? 'animate__infinite' : '',
    customDelay !== 0 ? `animate__delay-${customDelay}s` : '',
    'animate__faster',
    `animate__${animation}`,
  ].filter(className => className !== '');
}

/**
 * Registra eventos de una animacion
 * @param type
 * @param element
 * @param callback
 */
export const registerAnimationEvent = (type: 'start' | 'end', element: Element, callback: () => void) => {
  if (element !== null) {
    element.removeEventListener(type === 'start' ? 'animationstart' : 'animationend', callback);
    element.addEventListener(type === 'start' ? 'animationstart' : 'animationend', callback, { once: true });
  }
};

/**
 * Determina si entro en el viewport de mobile
 */
export const isMobileBreakpoint = () => {
  return window.innerWidth <= 1023;
};

/**
 * Scrollea la conversacion al final a otra posicion
 * @param offset
 * @param isMobile
 * @param force
 * @param callback
 */
export const scrollConversationToPosition = (offset: string | undefined, isMobile: boolean, force: boolean, callback: () => void | undefined = () => {}) => {
  const executeScroll = () => {
    const scrollContainer = document.getElementById('chat-conversation');
    const canScroll = scrollContainer?.getAttribute('auto-scrolleable') === 'true';
    const paddingTop = isMobile ? 8 : 8;
    if (scrollContainer !== null) {
      if (canScroll || force) {
        scrollContainer?.addEventListener(
          'scrollend',
          () => {
            callback();
          },
          { once: true },
        );

        const resolveOffset = (offset: string | undefined): number => {
          const scrollToElement = typeof offset === 'string',
            scrollToBottom = typeof offset === 'undefined',
            offsetTopElement = scrollToElement ? document.getElementById(offset)?.offsetTop ?? 0 : undefined;

          if (scrollToBottom) {
            return scrollContainer.scrollHeight;
          } else if (scrollToElement && typeof offsetTopElement !== 'undefined') {
            return offsetTopElement - paddingTop;
          } else {
            return scrollContainer.scrollHeight;
          }
        };

        const scrollPosition = resolveOffset(offset);
        if (scrollPosition >= 0) {
          //console.debug(`Scroll to ${scrollPosition} with offset ${offset} and force ${force}`);
          scrollContainer.scrollTop = scrollPosition;
        }
      } else {
        //console.debug("Ignore scroll, can't scroll");
      }
    } else {
      console.error('#chat-conversation is null');
    }
  };

  if (document.visibilityState === 'hidden') {
    document.addEventListener('visibilitychange', executeScroll, { once: true });
  } else {
    executeScroll();
  }
};

/**
 * Fuerza el foco en el textarea para facilitar la conversacion
 */
export const forceFocusChatTextarea = (isMobile: boolean) => {
  const enabledAutoFocus = isAppleDevice() || !isMobile;
  if (enabledAutoFocus) {
    window.setTimeout(() => {
      const textarea = document.getElementById('chat-textarea') as HTMLTextAreaElement;
      if (textarea !== null) {
        textarea.focus();
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      } else {
        console.error('Cant trigger focus textarea');
      }
    }, 500);
  }
};

/**
 * Reproduce el audio de envio o recepcion de mensaje
 * @param type
 * @param isMobile
 */
export const playAudio = (type: 'send' | 'receive', isMobile: boolean) => {
  if (!isMobile) {
    const id: string = {
      send: 'sendMessage',
      receive: 'receiveMessage',
    }[type];

    (document.getElementById(id) as HTMLAudioElement)?.play();
  }
};

/**
 * Anima los elementos de los contextos que esten pendientes
 * @param elements
 */
export const animateContexts = (elements: HTMLElement[]) => {
  const classToAnimate = getAnimationClass(0, 'fadeInUp', false);

  elements.forEach(node => {
    node.classList.remove(...classToAnimate);

    // THis is necessary for browser to apply animation
    // https://css-tricks.com/restart-css-animation/
    setTimeout(() => {
      node.classList.add(...classToAnimate);
    }, 100);
  });
};

/**
 * Calcula el alto del componente del chat
 * @param chatHostElement
 */
export const getChatHeight = (chatHostElement: HTMLElement | undefined) => {
  const shifu3Header = document.querySelector('.shifu-3-header');
  const windowHeight = window.visualViewport?.height ?? window.innerHeight;
  if (shifu3Header !== null) {
    const shifu3HeaderHeight = shifu3Header.clientHeight;

    // Calcula el alto especifico del chat
    if (!!chatHostElement) {
      return `${windowHeight - shifu3HeaderHeight}px`;
    }
  }

  return `${windowHeight}px`;
};

/**
 * Cambia el overflow del body para prevenir el dobble scroll
 * @param hide
 * @param isApp
 */
export const changeOverflowBody = (hide: boolean, isApp: boolean) => {
  // Para evitar romper el scroll en componentes externos.
  if (isApp && window?.Sofia?.alreadyLoaded) {
    document.body.style.overflow = 'hidden';
  } else {
    if (hide) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }
};

/**
 * Elimina elementos que no queremos de la landing de nymeria
 */
export const removeElementsNymeria = () => {
  document
    .querySelectorAll('.titles-module-ChatAIModule, #beta-headers, #subscription, #subscription-ab, .subscription-messages-container, .footer-seo-links-container, .landing-debug')
    ?.forEach(element => {
      element.remove();
    });
};

/**
 * Elimina el margen superior de inferior del contenedor de nymeria
 */
export const removeMarginsBymeria = () => {
  const element: HTMLDivElement | null = document.querySelector('.module-ChatAIModuleV2');
  if (element !== null) {
    element.style.marginTop = '0';
    element.style.marginBottom = '0';
  }
};

/**
 * Registra el handler del autoscroll para los mensajes
 */

export const registerAutoScrollHandler = (mode: SofiaMode) => {
  setTimeout(() => {
    const chatConversation = document.querySelector('.chat-conversation') as HTMLDivElement | null;
    const messagesContainer = (chatConversation?.querySelector('#chat-conversation') ?? null) as HTMLDivElement | null;
    const controlsContainer = document.querySelector('.scroll-messages-bottom-container') as HTMLDivElement | null;

    messagesContainer?.setAttribute('auto-scrolleable', 'true');
    messagesContainer?.addEventListener(
      'scroll',
      debounce(() => {
        const currentScrollPosition = Math.trunc(messagesContainer.scrollTop);
        const scrollHeight = messagesContainer.scrollHeight;
        const screenHeight = messagesContainer.clientHeight;
        const maxScrollPosition = Math.trunc(scrollHeight - screenHeight / 2);
        const alreadyScrolledScreenHeight = maxScrollPosition - currentScrollPosition >= screenHeight;

        messagesContainer.setAttribute('auto-scrolleable', !alreadyScrolledScreenHeight ? 'true' : 'false');

        if (!!controlsContainer) {
          toggleAnimation(controlsContainer, 'animate__fadeInDown', 'animate__fadeOutDown', alreadyScrolledScreenHeight);
        }
      }, 10),
    );
  }, 250);
};

/**
 * Implementa el manejo de ocultar las clases de animacion
 * @param element
 * @param animateEntrance
 * @param animateExit
 * @param isEntrance
 * @param cbFinishAnimation
 */
export const toggleAnimation = (element: HTMLDivElement | null, animateEntrance: string, animateExit: string, isEntrance: boolean, cbFinishAnimation: () => void = () => {}) => {
  if (!!element) {
    if (isEntrance) {
      // Remueve las clases para dar comienzo al ciclo de animacion
      element.classList.remove('animate__pending', 'hide');
      element.classList.remove(animateExit);
      element.classList.add(animateEntrance);
    } else {
      // Antes de quitar la animacion, verifica que efectivamente se haya animado previamente
      if (!element.classList.contains('animate__pending')) {
        registerAnimationEvent('end', element, () => {
          cbFinishAnimation();
        });

        element.classList.remove(animateEntrance);
        element.classList.add(animateExit);
      }
    }
  }
};

/*
 * Calcula el largo del texto para el view more y anima la expansion
 * @param descriptionElement
 */
export const toggleViewMoreAnimation = (descriptionElement: HTMLDivElement) => {
  const isCollapsed = descriptionElement.classList.contains('collapsed');
  if (isCollapsed) {
    descriptionElement.classList.remove('collapsed');
  } else {
    descriptionElement.addEventListener(
      'transitionend',
      () => {
        descriptionElement.classList.add('collapsed');
      },
      { once: true },
    );
  }
  descriptionElement.style.maxHeight = isCollapsed ? '80px' : descriptionElement.scrollHeight + 'px';
  descriptionElement.getBoundingClientRect();
  descriptionElement.style.maxHeight = isCollapsed ? descriptionElement.scrollHeight + 'px' : '80px';
};

/**
 * Carga un chat sin refrescar la pagina
 * @param newChat
 */
export const loadChatByUser = (newChat: boolean) => {
  const event = new CustomEvent('reloadChat', {
    detail: newChat,
    bubbles: true,
    cancelable: true,
    composed: true,
  });
  document.dispatchEvent(event);
};

/**
 * Calcula la altura del componente del chat
 * @param sofiaMode
 * @param isApp
 * @param isShowDownloaderApp
 * @param el
 */
export const getChatAssistanceHeight = (sofiaMode: SofiaMode, isApp: boolean, isShowDownloaderApp: boolean, el?: HTMLElement) => {
  // Es importante que esto quede primero, porque en cualquier caso que sea bubble debe tener el 100%.
  if (sofiaMode === 'BUBBLE') {
    return '100%';
  } else if (isApp) {
    return '100vh';
  } else if (isShowDownloaderApp) {
    return 'auto';
  } else {
    return getChatHeight(el);
  }
};

/**
 * Determina si se debe mostrar los terminos y condiciones
 * @param termsAndConditionsState
 * @param enableTermsAndConditionsModal
 */
export const isShowTermsAndConditions = (termsAndConditionsState: TermsAndConditionsState, enableTermsAndConditionsModal: boolean) => {
  return !termsAndConditionsState.accepted && enableTermsAndConditionsModal;
};

/**
 * Determina si el chat-assistance esta cargado por medio del chat-assistance-floating
 */
export const isChatAssistanceInnerFloating = (): boolean => {
  return document.querySelector('chat-assistance')?.closest('chat-assistance-floating') !== null;
};

/**
 * Dispara un resize para ajustar la UI
 */
export const triggerResize = () => {
  window.dispatchEvent(new Event('resize'));
};

/**
 * Clases para aplicar animacion a los nuevos boards
 */
export const CLASSNAME_ANIMATES_CONTEXTS = {
  applyAnimation: 'animate__pending',
  componentClass: 'grid__to__animate',
};

/**
 * Abre un dialogo modal por medio de su id
 * @param dialogId
 */
export const openDialog = (dialogId: string, retry = true) => {
  const element = document.getElementById(dialogId) as HTMLDialogElement | null;

  console.debug('try open dialog with id:', dialogId, ' element:', element);

  // If the element is not yet in the DOM (e.g., Stencil hasn't rendered it),
  // retry once on the next tick to avoid race conditions when opening dialogs.
  if (!element) {
    if (retry) {
      setTimeout(() => openDialog(dialogId, false), 0);
    } else {
      console.warn(`openDialog: element with id ${dialogId} not found after retry.`);
    }
    return;
  }

  changeOverflowBody(true, initParams.isApp);

  element.showModal();

  if (element.classList.contains('eva-3-modal-side-sheet')) {
    element.classList.add('-show-modal-side-sheet');
  }

  if (element.classList.contains('eva-3-modal')) {
    element.classList.add('-show-modal');
  }
};

/**
 * Cierra un dialogo modal por medio de su id con animaciones
 * @param dialogId
 */
export const closeDialog = (dialogId: string, afterClose: () => void = () => {}) => {
  const element = document.getElementById(dialogId) as HTMLDialogElement | null;

  console.debug('try close dialog with id:', dialogId, ' element:', element);

  if (!element) return;

  changeOverflowBody(false, initParams.isApp);

  // Handle side sheet animation
  if (element.classList.contains('eva-3-modal-side-sheet')) {
    element.classList.remove('-show-modal-side-sheet');
    // Wait for animation to complete before closing
    setTimeout(() => {
      element.close();
      afterClose();
    }, 300); // Standard animation duration
  }
  // Handle modal animation
  else if (element.classList.contains('eva-3-modal')) {
    element.classList.remove('-show-modal');
    // Wait for animation to complete before closing
    setTimeout(() => {
      element.close();
      afterClose();
    }, 300); // Standard animation duration
  }
  // Fallback for dialogs without animations
  else {
    element.close();
    afterClose();
  }
};
